import {
  Box,
  Stack,
  Text,
  Icon,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
  DataTable,
  DataTableTable,
  Label,
  Tooltip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/table-core';
import type { StatusIndicatorProps } from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthProcessingCategory } from '@/services/api/services-api';
import {
  CATEGORY_LABELS,
  EGRESS_CATEGORY_LABELS,
  delayStatus,
  formatCompactDuration,
  SERVICE_INFO_TOOLTIPS,
  CATEGORY_IMPACT,
  useWorkspaceHealthSimulation,
  useEnvHealth,
  DELAY_STATUS_LABELS,
} from '@/features/workspace-health';
import * as styles from './index.css';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { calc } from '@vanilla-extract/css-utils';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

const columnHelper = createColumnHelper<ServicesApiEnvHealthProcessingCategory>();

const columns = [
  columnHelper.accessor('delay_seconds', {
    header: () => {
      return (
        <InlineStack gap="050" alignBlock="center">
          <Text text="product-p-active">Status</Text>
          <Popover placement="right-start" shouldTriggerOnClick={false} shouldTriggerOnHover={true}>
            <PopoverTrigger cursor="help">
              <Icon name="info" size="sm" color="subtle" aria-label="View status explanation" display="block" />
            </PopoverTrigger>
            <PopoverContent style={{ width: calc(themeVars.space['1600']).multiply(2.5).toString() }}>
              <Stack gap="100">
                <Stack gap="050" alignBlock="center">
                  <Box>
                    <Label color="green" showStatusIndicator>
                      {DELAY_STATUS_LABELS.normal}
                    </Label>
                  </Box>
                  <Text color="subtle">no backlog or delay under 10 seconds — the service is keeping up.</Text>
                </Stack>
                <Stack gap="050" alignBlock="center">
                  <Box>
                    <Label color="yellow" showStatusIndicator>
                      {DELAY_STATUS_LABELS.slight_delay}
                    </Label>
                  </Box>
                  <Text color="subtle">10 seconds to about 1 minute — the service is slightly backed up.</Text>
                </Stack>
                <Stack gap="050" alignBlock="center">
                  <Box>
                    <Label color="yellow" showStatusIndicator>
                      {DELAY_STATUS_LABELS.delayed}
                    </Label>
                  </Box>
                  <Text color="subtle">1 minute to about 5 minutes — the service is backed up.</Text>
                </Stack>
                <Stack gap="050" alignBlock="center">
                  <Box>
                    <Label color="red" showStatusIndicator>
                      {DELAY_STATUS_LABELS.major_delay}
                    </Label>
                  </Box>
                  <Text color="subtle">5+ minutes — the service is heavily backed up.</Text>
                </Stack>
              </Stack>
            </PopoverContent>
          </Popover>
        </InlineStack>
      );
    },
    cell: (info) => {
      const pending = info.row.original.pending ?? 0;
      const delaySeconds = info.row.original.delay_seconds;
      const status = delayStatus(delaySeconds, pending);
      return (
        <Label color={status.color} showStatusIndicator>
          {status.label}
        </Label>
      );
    },
    meta: {
      width: 144,
    },
  }),
  columnHelper.accessor('name', {
    header: 'Service name',
    cell: (info) => {
      const name = info.getValue();
      const labels = { ...EGRESS_CATEGORY_LABELS, ...CATEGORY_LABELS };

      const displayLabel = labels[name] ?? name;
      const tooltip = SERVICE_INFO_TOOLTIPS[name] ?? displayLabel;
      return (
        <InlineStack gap="050" alignBlock="center">
          <Text text="product-p">{displayLabel}</Text>
          <Tooltip content={tooltip}>
            <Icon name="info" size="sm" color="subtle" cursor="help" />
          </Tooltip>
        </InlineStack>
      );
    },
  }),
];

const STATUS_INDICATOR_COLOR: Record<string, StatusIndicatorProps['color']> = {
  green: 'green',
  yellow: 'yellow',
  red: 'red',
};

function DistressedRow({
  category,
  labels,
}: {
  category: ServicesApiEnvHealthProcessingCategory;
  labels?: Record<string, string>;
}) {
  const label = (labels ?? CATEGORY_LABELS)[category.name] ?? category.name;
  const status = delayStatus(category.delay_seconds, category.pending);
  const duration =
    category.delay_seconds != null && category.delay_seconds >= 10
      ? formatCompactDuration(category.delay_seconds)
      : null;
  const statusText = duration != null ? `${status.label} for ${duration}` : status.label;
  const impact = CATEGORY_IMPACT[category.name];

  return (
    <Stack gap="050" py="150" px="300" className={styles.categoryGroup}>
      <InlineStack gap="200" alignBlock="center">
        <StatusIndicator color={STATUS_INDICATOR_COLOR[status.color] ?? 'yellow'} />
        <Box flex={1}>
          <Text size="s" weight="semibold">
            {label}
          </Text>
        </Box>
        <Text size="xs" color="subtle">
          {statusText}
        </Text>
      </InlineStack>
      {impact != null ? (
        <Box pl="300">
          <Text size="xs" color="subtle">
            {impact}
          </Text>
        </Box>
      ) : null}
    </Stack>
  );
}

function isDistressed(cat: ServicesApiEnvHealthProcessingCategory): boolean {
  const status = delayStatus(cat.delay_seconds, cat.pending);
  return status.color !== 'green';
}

const initialSorting = {
  id: 'delay_seconds',
  desc: true,
};

export function ServicesTabContent() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data?.body;
  const allServices = [...(data?.processing.categories ?? []), ...(data?.egress.categories ?? [])];

  const distressedCategories = [
    ...(data?.processing.categories?.filter(isDistressed) ?? []),
    ...(data?.egress.categories?.filter(isDistressed) ?? []),
  ].sort((a, b) => (b.delay_seconds ?? 0) - (a.delay_seconds ?? 0));

  return (
    <Stack gap="200">
      {distressedCategories.length > 0 ? (
        <Panel withSectionBorders>
          <PanelHeader>
            <PanelTitle>Services in distress</PanelTitle>
          </PanelHeader>
          <PanelSection withPadding={false}>
            <Stack>
              {distressedCategories.map((cat) => {
                const isEgress = data?.egress.categories?.includes(cat) ?? false;
                return (
                  <DistressedRow key={cat.name} category={cat} labels={isEgress ? EGRESS_CATEGORY_LABELS : undefined} />
                );
              })}
            </Stack>
          </PanelSection>
        </Panel>
      ) : null}
      <Panel>
        <PanelHeader>
          <PanelTitle>All services</PanelTitle>
        </PanelHeader>
        <PanelSection withPadding={false}>
          <DataTable
            data={allServices}
            columns={columns}
            caption="All services"
            isLoading={healthQuery.isLoading}
            withSorting={true}
            sorting={{
              value: initialSorting,
            }}
          >
            <DataTableTable />
          </DataTable>
        </PanelSection>
      </Panel>
    </Stack>
  );
}
